/* eslint-disable camelcase */
import { useState, useEffect, useContext, useMemo } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Grid, Typography, Autocomplete, Badge, Button } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StorefrontIcon from "@mui/icons-material/Storefront"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"

import CatalogoProductos from "./components/CatalogoProductos"
import CarritoResumen from "./components/CarritoResumen"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearSolicitudEgresoCliente = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const globalContext = useContext(GlobalContext)

  const [carritoOpen, setCarritoOpen] = useState(false)
  const fechaHoy = new Date().toISOString().split("T")[0]

  // Paso 1: Estado unificado ampliado con Agencias y Contactos
  const [formData, setFormData] = useState({
    tsolcodigo: "",
    tsolInEg: "",
    ciaaproautinsol: 0,
    ciamostrarstock: 0, // NUEVO: -1 = solo con stock, 0 = todos
    motcodigo: "",
    sgadescri: "",
    sgacomenenvio: "",
    clicodigo: "",
    procodigo: "",
    sgafecenvio: fechaHoy,
    sgahorenvio: "",
    agencodigosol: "", // Agencia Solicita
    agencodigoenv: "", // Agencia a Enviar
    condescrienv: "", // Contacto Recibe
    detalle: [],
  })

  // CARGA DE CATÁLOGOS
  const { data: catData, isLoading: loadCat } = useQuery({
    queryKey: ["catalogosEgresoCliente"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudEgrCli/getCatalogosEgreso", {})

        let catalogos = {}
        const res = response?.data?.data || response?.data || response || {}

        if (res.catalogo_productos) {
          catalogos = res
        } else if (res.data && res.data.catalogo_productos) {
          catalogos = res.data
        }

        return catalogos
      } catch (error) {
        return {
          tipos_solicitud: [],
          motivos: [],
          catalogo_productos: [],
          proveedores: [],
          agencias: [],
          contactos: [],
          cliente_asignado: null,
        }
      }
    },
    staleTime: 0,
  })

  // Extracción Defensiva
  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []

  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaMotivos = extractArray(catData?.motivos)
  const listaProveedores = extractArray(catData?.proveedores)
  const listaAgencias = extractArray(catData?.agencias)
  const listaContactosGlobal = extractArray(catData?.contactos)
  const catalogoProductos = extractArray(catData?.catalogo_productos)
  const clienteAsignado = catData?.cliente_asignado || null

  // ==========================================================================
  // NUEVO: FILTRADO DEL CATÁLOGO SEGÚN ciamostrarstock DEL TIPO SELECCIONADO
  // - Sin tipo seleccionado  → mostrar todos
  // - ciamostrarstock === -1 → solo stock_actual > 0
  // - ciamostrarstock === 0  → mostrar todos
  // ==========================================================================
  const catalogoProductosFiltrado = useMemo(() => {
    if (!catalogoProductos || catalogoProductos.length === 0) return []

    // Si no hay tipo de solicitud seleccionado, mostramos todo
    if (!formData.tsolcodigo) return catalogoProductos

    // Normalizar el flag (puede venir como número o string)
    const flag = Number(formData.ciamostrarstock)
    const soloConStock = flag === -1

    if (!soloConStock) return catalogoProductos

    return catalogoProductos.filter((p) => Number(p.stock_actual) > 0)
  }, [catalogoProductos, formData.tsolcodigo, formData.ciamostrarstock])

  // Filtramos la lista de contactos en tiempo real según la agencia de envío seleccionada
  const listaContactosFiltrada = listaContactosGlobal.filter(
    (c) => String(c.agencodigo) === String(formData.agencodigoenv),
  )

  // Sincronizar el clicodigo
  useEffect(() => {
    if (clienteAsignado?.id && formData.clicodigo !== clienteAsignado.id) {
      setFormData((prev) => ({ ...prev, clicodigo: clienteAsignado.id }))
    }
  }, [clienteAsignado, formData.clicodigo])

  // ==========================================================================
  // GENERACIÓN DE REPORTE PDF (EGRESO)
  // ==========================================================================
  const handleImprimirReporte = (solnumero) => {
    const doc = new JsPDF("p", "pt", "a4")
    const nombreCiaDinamic =
      globalContext?.ciaSeleccionada?.label ||
      globalContext?.ciaSeleccionada?.cianombre ||
      globalContext?.empresaSeleccionada?.label ||
      globalContext?.seleccion?.cianombre ||
      "SISTEMA LOGÍSTICO MERCATTI"
    const tipoSol = listaTiposSolicitud.find((t) => t.tsolcodigo === formData.tsolcodigo)?.label || ""
    const motivo = listaMotivos.find((m) => m.id === formData.motcodigo)?.label || ""
    const ageSol = listaAgencias.find((a) => a.id === formData.agencodigosol)?.label || ""
    const ageEnv = listaAgencias.find((a) => a.id === formData.agencodigoenv)?.label || ""

    const truncateTxt = (txt, max) => (txt.length > max ? txt.substring(0, max) + "..." : txt)

    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135)
    doc.text(nombreCiaDinamic, 40, 40)
    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text(`SOLICITUD DE EGRESO NRO: ${solnumero}`, 40, 65)

    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)
    doc.text(truncateTxt(`Cliente Asociado: ${clienteAsignado?.label || formData.clicodigo}`, 70), 40, 95)
    doc.text(truncateTxt(`Tipo de Solicitud: ${tipoSol}`, 70), 40, 110)
    doc.text(truncateTxt(`Motivo de Egreso: ${motivo}`, 70), 40, 125)

    doc.text(truncateTxt(`Agencia Solicita: ${ageSol}`, 70), 40, 140)
    doc.text(truncateTxt(`Agencia Envío: ${ageEnv}`, 70), 40, 155)
    doc.text(truncateTxt(`Contacto Recibe: ${formData.condescrienv}`, 70), 40, 170)

    doc.text(
      `Fecha/Hora Envío: ${dayjs(formData.sgafecenvio).format("DD/MM/YYYY")} a las ${formData.sgahorenvio}`,
      555,
      95,
      { align: "right" },
    )
    doc.text(`Fecha Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 555, 110, { align: "right" })

    doc.setFont("helvetica", "bold")
    doc.text("Descripción General:", 40, 190)
    doc.setFont("helvetica", "normal")
    const splitDesc = doc.splitTextToSize(formData.sgadescri, 500)
    doc.text(splitDesc, 40, 205)

    const tableColumn = ["Ítem", "Código", "Descripción", "Cantidad Solicitada"]
    const tableRows = formData.detalle.map((prod, idx) => [
      idx + 1,
      prod.artcodigo,
      prod.artdescri,
      prod.cantidad_solicitada,
    ])

    autoTable(doc, {
      startY: 220 + splitDesc.length * 10,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center", cellWidth: 40 },
        1: { halign: "left", cellWidth: 100 },
        3: { halign: "center", cellWidth: 100, fontStyle: "bold" },
      },
    })
    doc.save(`Solicitud_Egreso_${solnumero}.pdf`)
  }

  const { mutateAsync: SaveSolicitud, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingSolEgrCli"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudEgrCli/createSolicitudEgreso", data)
      return response.data
    },
    showError: "modal",
    showSuccess: false,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries()

      const numeroGenerado = response?.data?.solnumero || response?.solnumero || ""

      const confirmacion = await Swal.fire({
        title: "¡Solicitud Registrada!",
        html: `<p>Se ha generado exitosamente la Solicitud Nro: <strong>${numeroGenerado}</strong></p><p>¿Desea imprimir el comprobante?</p>`,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Sí, imprimir",
        cancelButtonText: "No, salir",
        confirmButtonColor: "#196C87",
        cancelButtonColor: "#7f8c8d",
        reverseButtons: true,
        allowOutsideClick: false,
      })

      if (confirmacion.isConfirmed) {
        handleImprimirReporte(numeroGenerado)
        navigate(-1)
      } else {
        navigate(-1)
      }
    },
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
  }

  const handleDateChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleTipoSolicitudChange = (event, newValue) => {
    const nuevoTipo = newValue ? String(newValue.id) : ""
    const nuevoCodigo = newValue ? String(newValue.tsolcodigo) : ""
    const autoAprob = newValue?.ciaaproautinsol || 0

    // NUEVO: normalizar ciamostrarstock del tipo seleccionado
    const mostrarStock =
      newValue?.ciamostrarstock !== undefined && newValue?.ciamostrarstock !== null
        ? Number(newValue.ciamostrarstock) === -1
          ? -1
          : 0
        : 0

    setFormData((prev) => {
      // Si el nuevo tipo exige "solo con stock", limpiamos del carrito los productos sin stock
      let nuevoDetalle = prev.detalle
      if (mostrarStock === -1 && prev.detalle.length > 0) {
        const productosSinStockEnCarrito = prev.detalle.filter((item) => Number(item.stock_actual) <= 0)
        if (productosSinStockEnCarrito.length > 0) {
          nuevoDetalle = prev.detalle.filter((item) => Number(item.stock_actual) > 0)
          showWarning(
            `Se removieron ${productosSinStockEnCarrito.length} producto(s) del carrito porque el tipo seleccionado solo permite artículos con stock.`,
          )
        }
      }

      return {
        ...prev,
        tsolInEg: nuevoTipo,
        tsolcodigo: nuevoCodigo,
        ciaaproautinsol: autoAprob,
        ciamostrarstock: mostrarStock,
        detalle: nuevoDetalle,
      }
    })
  }

  const handleCheckout = async () => {
    if (!formData.tsolcodigo) return showWarning("Debe seleccionar un Tipo de Solicitud permitido.")
    if (!formData.motcodigo) return showWarning("Debe seleccionar un Motivo para su egreso.")
    if (!formData.agencodigosol) return showWarning("Debe seleccionar la Agencia que Solicita.")
    if (!formData.agencodigoenv) return showWarning("Debe seleccionar la Agencia a la que se Envía.")
    if (!formData.condescrienv) return showWarning("Debe seleccionar el Contacto que Recibe.")
    if (!formData.sgafecenvio) return showWarning("Debe especificar la Fecha estimada de envío.")
    if (!formData.sgahorenvio) return showWarning("Debe especificar la Hora estimada de envío.")
    if (!formData.sgadescri.trim())
      return showWarning("Por favor, agregue una descripción u observación a su solicitud.")
    if (formData.detalle.length === 0) return showWarning("Su carrito de solicitud está vacío.")

    const carritoLimpio = formData.detalle.filter((item) => parseFloat(item.cantidad_solicitada) > 0)
    if (carritoLimpio.length === 0) return showWarning("Todos los productos en su carrito tienen cantidad 0.")

    // NUEVO: validación de seguridad si el tipo exige "solo con stock"
    if (Number(formData.ciamostrarstock) === -1) {
      const sinStock = carritoLimpio.filter((item) => Number(item.stock_actual) <= 0)
      if (sinStock.length > 0) {
        return showWarning(
          `El tipo de solicitud seleccionado solo permite productos con stock. Retire del carrito: ${sinStock
            .map((i) => i.artcodigo)
            .join(", ")}`,
        )
      }
    }

    await SaveSolicitud({ ...formData, detalle: carritoLimpio })
  }

  const loadingGlobal = loadCat || isSaving
  const totalItemsEnCarrito = formData.detalle.reduce(
    (acc, item) => acc + (parseFloat(item.cantidad_solicitada) > 0 ? 1 : 0),
    0,
  )

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              <Badge badgeContent={totalItemsEnCarrito} color="error">
                <ShoppingCartIcon />
              </Badge>
            }
            onClick={() => setCarritoOpen(true)}
            disabled={loadingGlobal}
            sx={{ fontWeight: "bold", borderRadius: 8, px: 4 }}
          >
            Ver Mi Solicitud
          </Button>
        </Box>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "0 30px 30px 30px" }}>
          <StorefrontIcon sx={{ fontSize: 40, color: "#196C87", mr: 1 }} />
          <Typography variant="h4" color="primary" fontWeight="bold">
            Crear Solicitud de Egreso
          </Typography>
        </div>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, background: "white", mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ borderBottom: "2px solid #196C87", pb: 1, mb: 3 }}
            >
              Información de Egreso
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cliente Asociado *"
                  value={clienteAsignado ? clienteAsignado.label : "Cargando..."}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || listaTiposSolicitud.length === 0}
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.tsolcodigo) === String(formData.tsolcodigo)) ||
                    (formData.tsolcodigo
                      ? { id: formData.tsolInEg, tsolcodigo: formData.tsolcodigo, label: "Cargando..." }
                      : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.tsolcodigo) === String(value?.tsolcodigo)}
                  onChange={handleTipoSolicitudChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal}
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaMotivos.find((c) => String(c.id) === String(formData.motcodigo)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, motcodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo de Egreso *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              {/* BLOQUE NUEVO: AGENCIAS Y CONTACTO */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || listaAgencias.length === 0}
                  options={listaAgencias}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaAgencias.find((a) => String(a.id) === String(formData.agencodigosol)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, agencodigosol: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Agencia que Solicita *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || listaAgencias.length === 0}
                  options={listaAgencias}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaAgencias.find((a) => String(a.id) === String(formData.agencodigoenv)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) => {
                    // Al cambiar la agencia, limpiamos el contacto porque sus contactos ya no son válidos
                    setFormData((prev) => ({
                      ...prev,
                      agencodigoenv: newVal ? String(newVal.id) : "",
                      condescrienv: "",
                    }))
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Agencia de Envío *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || !formData.agencodigoenv || listaContactosFiltrada.length === 0}
                  options={listaContactosFiltrada}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaContactosFiltrada.find((c) => String(c.id) === String(formData.condescrienv)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, condescrienv: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Contacto que Recibe *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Envío *"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: "es-EC" }}
                  value={formData.sgafecenvio}
                  onChange={(e) => handleDateChange("sgafecenvio", e.target.value)}
                  disabled={loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Envío *"
                  InputLabelProps={{ shrink: true }}
                  value={formData.sgahorenvio}
                  onChange={(e) => handleDateChange("sgahorenvio", e.target.value)}
                  disabled={loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  disabled={loadingGlobal}
                  fullWidth
                  label="Descripción / Observación *"
                  value={formData.sgadescri}
                  onChange={(e) => handleInputChange("sgadescri", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                  placeholder="Ej. Envío de material a sucursal norte..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={loadingGlobal}
                  fullWidth
                  label="Instrucciones para el Envío (Opcional)"
                  value={formData.sgacomenenvio}
                  onChange={(e) => handleInputChange("sgacomenenvio", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                  placeholder="Ej. Cargar en camión placa XXX-123..."
                />
              </Grid>
            </Grid>
          </Paper>

          <CatalogoProductos
            catalogo={catalogoProductosFiltrado}
            carrito={formData.detalle}
            setFormData={setFormData}
            loading={loadCat}
          />
        </Box>
      </div>

      <CarritoResumen
        open={carritoOpen}
        onClose={() => setCarritoOpen(false)}
        carrito={formData.detalle}
        setFormData={setFormData}
        onCheckout={handleCheckout}
        isSaving={isSaving}
      />
    </ThemeProvider>
  )
}

export default CrearSolicitudEgresoCliente
